import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { supabase, describeError, type Row } from '@/lib/supabase'
import { pipelineKeys } from '@/features/pipeline/queries'
import { CLIENT_FIELDS, PROJECT_FIELDS, quoteKeys, type DocumentClient, type DocumentProject } from '@/features/quotes/queries'
import type { PlannedInstalment, PlannedLine, PlanStep } from '@/lib/invoicing'
import { isFilled, lineAmount, rescaleInstalments, type EditableLine, type LineTotals } from '@/lib/lineItems'

export type Invoice = Row<'invoices'>
export type InvoiceLine = Row<'invoice_line_items'>
export type Payment = Row<'payments'>
export type Instalment = Row<'invoice_instalments'>
export type PaymentPlan = Row<'payment_plans'> & { steps: Array<Row<'payment_plan_steps'>> }

export interface InvoiceWithDetail extends Invoice {
  line_items: InvoiceLine[]
  payments: Payment[]
  instalments: Instalment[]
  /** The client (29 Oct 2026). */
  contact: DocumentClient | null
  /** The project, if any. */
  job: DocumentProject | null
}

/** An invoice with just enough of its wedding to list it on its own. */
export interface InvoiceListItem extends Invoice {
  instalments: Instalment[]
  contact: { first_name: string; last_name: string | null } | null
  job: {
    id: string
    job_number: number
    event_date: string
    primary_contact: { first_name: string; last_name: string | null } | null
    partner_contact: { first_name: string; last_name: string | null } | null
  } | null
}

export const invoiceKeys = {
  all: ['invoices'] as const,
  forJob: (jobId: string) => ['invoices', 'job', jobId] as const,
  detail: (id: string) => ['invoice', id] as const,
  plans: ['payment-plans'] as const,
}

const DETAIL_SELECT = `*, line_items:invoice_line_items(*), payments(*), instalments:invoice_instalments(*),
  contact:contacts!invoices_contact_id_fkey ( ${CLIENT_FIELDS} ), job:jobs!invoices_job_id_fkey ( ${PROJECT_FIELDS} )`

export function useJobInvoices(jobId: string | undefined) {
  return useQuery({
    queryKey: invoiceKeys.forJob(jobId ?? ''),
    enabled: Boolean(jobId),
    queryFn: async (): Promise<InvoiceWithDetail[]> => {
      const { data, error } = await supabase
        .from('invoices')
        .select(DETAIL_SELECT)
        .eq('job_id', jobId ?? '')
        .order('invoice_number')
      if (error) throw error
      return data as unknown as InvoiceWithDetail[]
    },
  })
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: invoiceKeys.detail(id ?? ''),
    enabled: Boolean(id),
    queryFn: async (): Promise<InvoiceWithDetail> => {
      const { data, error } = await supabase.from('invoices').select(DETAIL_SELECT).eq('id', id ?? '').single()
      if (error) throw error
      const invoice = data as unknown as InvoiceWithDetail
      invoice.line_items.sort((a, b) => a.sort_order - b.sort_order)
      invoice.payments.sort((a, b) => a.paid_on.localeCompare(b.paid_on))
      invoice.instalments.sort((a, b) => a.position - b.position)
      return invoice
    },
  })
}

/** Every invoice, for the money screen. One query, no waterfall. */
export function useAllInvoices() {
  return useQuery({
    queryKey: invoiceKeys.all,
    queryFn: async (): Promise<InvoiceListItem[]> => {
      const { data, error } = await supabase
        .from('invoices')
        .select(
          '*, instalments:invoice_instalments(*), contact:contacts!invoices_contact_id_fkey(first_name, last_name), job:jobs!invoices_job_id_fkey(id, job_number, event_date, primary_contact:contacts!jobs_primary_contact_id_fkey(first_name, last_name), partner_contact:contacts!jobs_partner_contact_id_fkey(first_name, last_name))',
        )
        .order('due_date')
      if (error) throw error
      return data as unknown as InvoiceListItem[]
    },
  })
}

/** Her named plans with their steps, default first. */
export function usePaymentPlans() {
  return useQuery({
    queryKey: invoiceKeys.plans,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<PaymentPlan[]> => {
      const { data, error } = await supabase
        .from('payment_plans')
        .select('*, steps:payment_plan_steps(*)')
        .eq('active', true)
        .order('sort_order')
      if (error) throw error
      const plans = data as unknown as PaymentPlan[]
      for (const plan of plans) plan.steps.sort((a, b) => a.position - b.position)
      return plans.sort((a, b) => Number(b.is_default) - Number(a.is_default))
    },
  })
}

export function planSteps(plan: PaymentPlan): PlanStep[] {
  return plan.steps.map((step) => ({
    label: step.label,
    share: step.share === null ? null : Number(step.share),
    due_rule: step.due_rule,
    offset_days: step.offset_days,
  }))
}

function useInvalidate() {
  const queryClient = useQueryClient()
  return (jobId: string | null, invoiceId?: string) => {
    void queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
    if (jobId) {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.forJob(jobId) })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.job(jobId) })
    }
    void queryClient.invalidateQueries({ queryKey: pipelineKeys.board })
    if (invoiceId) void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) })
  }
}

interface NewInvoice {
  kind: Invoice['kind']
  issueDate: string
  subtotal: number
  vat: number
  total: number
  lines: PlannedLine[]
  instalments: PlannedInstalment[]
  planName: string | null
}

async function insertInvoice(
  jobId: string | null,
  quoteId: string | null,
  planned: NewInvoice,
  staffId: string,
  contactId: string | null = null,
): Promise<string> {
  const last = planned.instalments[planned.instalments.length - 1]
  // The client: given, or the project's main contact.
  let client = contactId
  if (!client && jobId) {
    const { data: job, error: jobError } = await supabase.from('jobs').select('primary_contact_id').eq('id', jobId).single()
    if (jobError) throw jobError
    client = job.primary_contact_id
  }
  const { data, error } = await supabase
    .from('invoices')
    .insert({
      contact_id: client,
      job_id: jobId,
      quote_id: quoteId,
      kind: planned.kind,
      issue_date: planned.issueDate,
      due_date: last?.dueDate ?? planned.issueDate,
      subtotal: planned.subtotal,
      vat: planned.vat,
      total: planned.total,
      payment_plan_name: planned.planName,
      created_by: staffId,
    })
    .select('id')
    .single()
  if (error) throw error

  if (planned.lines.length > 0) {
    const { error: linesError } = await supabase.from('invoice_line_items').insert(
      planned.lines.map((line, index) => ({
        invoice_id: data.id,
        description: line.description,
        detail: line.detail,
        quantity: line.quantity,
        unit_price: line.unitPrice,
        line_total: line.lineTotal,
        sort_order: index,
      })),
    )
    if (linesError) throw linesError
  }

  await writeInstalments(data.id, planned.instalments)
  return data.id
}

async function writeInstalments(invoiceId: string, instalments: PlannedInstalment[]) {
  const { error: clearError } = await supabase.from('invoice_instalments').delete().eq('invoice_id', invoiceId)
  if (clearError) throw clearError
  if (instalments.length === 0) return
  const { error } = await supabase.from('invoice_instalments').insert(
    instalments.map((item, index) => ({
      invoice_id: invoiceId,
      position: index + 1,
      label: item.label,
      amount: item.amount,
      due_date: item.dueDate,
    })),
  )
  if (error) throw error
  const last = instalments[instalments.length - 1]
  if (last) {
    const { error: dueError } = await supabase.from('invoices').update({ due_date: last.dueDate }).eq('id', invoiceId)
    if (dueError) throw dueError
  }
}

export interface DeliverableSeed {
  title: string
  kind: 'photo' | 'film' | 'album' | 'other'
}

/**
 * Books the job from an accepted quote: marks the quote accepted, raises ONE
 * invoice with the quote's lines and the chosen payment plan, copies the
 * agreed numbers onto the job, and fills in the deliverables checklist and the
 * delivery deadlines if the job does not have them yet.
 */
export function useBookFromQuote() {
  const invalidate = useInvalidate()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      jobId: string | null
      contactId: string | null
      quoteId: string
      packageId: string | null
      invoice: Omit<NewInvoice, 'kind'>
      deliverables: DeliverableSeed[]
      photosDueDate: string | null
      filmDueDate: string | null
      staffId: string
    }) => {
      const { error: quoteError } = await supabase.from('quotes').update({ status: 'accepted' }).eq('id', input.quoteId)
      if (quoteError) throw quoteError

      await insertInvoice(input.jobId, input.quoteId, { ...input.invoice, kind: 'booking' }, input.staffId, input.contactId)
      // A quote without a project only raises the invoice: there is nothing to book.
      if (!input.jobId) return
      const jobId = input.jobId

      const first = input.invoice.instalments[0]
      const last = input.invoice.instalments[input.invoice.instalments.length - 1]

      const { data: job, error: readError } = await supabase
        .from('jobs')
        .select('photos_due_date, film_due_date')
        .eq('id', jobId)
        .single()
      if (readError) throw readError

      const { error: jobError } = await supabase
        .from('jobs')
        .update({
          agreed_total: input.invoice.total,
          package_id: input.packageId,
          deposit_amount: first?.amount ?? null,
          deposit_status: 'invoiced',
          balance_due_date: last?.dueDate ?? null,
          photos_due_date: job.photos_due_date ?? input.photosDueDate,
          film_due_date: job.film_due_date ?? input.filmDueDate,
        })
        .eq('id', jobId)
      if (jobError) throw jobError

      const { count, error: countError } = await supabase
        .from('job_deliverables')
        .select('id', { count: 'exact', head: true })
        .eq('job_id', jobId)
      if (countError) throw countError
      if ((count ?? 0) === 0 && input.deliverables.length > 0) {
        const { error: deliverablesError } = await supabase.from('job_deliverables').insert(
          input.deliverables.map((item, index) => ({ job_id: jobId, title: item.title, kind: item.kind, sort_order: index })),
        )
        if (deliverablesError) throw deliverablesError
      }
    },
    onSuccess: (_data, input) => {
      invalidate(input.jobId)
      void queryClient.invalidateQueries({ queryKey: ['quotes'] })
      void queryClient.invalidateQueries({ queryKey: quoteKeys.detail(input.quoteId) })
      if (input.jobId) void queryClient.invalidateQueries({ queryKey: ['job-deliverables', input.jobId] })
      toast.success('Booked: invoice raised with its payment plan')
    },
    onError: (error) => {
      toast.error('Could not raise the invoice', { description: describeError(error) })
    },
  })
}

/** Replaces a draft invoice's schedule: another plan, or a custom one. */
export function useReplaceInstalments() {
  const invalidate = useInvalidate()

  return useMutation({
    mutationFn: async (input: { invoiceId: string; jobId: string | null; instalments: PlannedInstalment[]; planName: string | null }) => {
      await writeInstalments(input.invoiceId, input.instalments)
      const { error } = await supabase.from('invoices').update({ payment_plan_name: input.planName }).eq('id', input.invoiceId)
      if (error) throw error
    },
    onSuccess: (_data, input) => {
      invalidate(input.jobId, input.invoiceId)
      toast.success('Payment plan updated')
    },
    onError: (error) => {
      toast.error('Could not change the payment plan', { description: describeError(error) })
    },
  })
}

export function useUpdateInvoice() {
  const invalidate = useInvalidate()

  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string
      jobId: string | null
      patch: Partial<Pick<Invoice, 'status' | 'note' | 'void_reason' | 'due_date'>>
      success?: string
    }) => {
      const { error } = await supabase.from('invoices').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: (_data, variables) => {
      invalidate(variables.jobId, variables.id)
      if (variables.success) toast.success(variables.success)
    },
    onError: (error) => {
      toast.error('Could not update the invoice', { description: describeError(error) })
    },
  })
}

export function useDeleteDraftInvoice() {
  const invalidate = useInvalidate()

  return useMutation({
    mutationFn: async ({ id }: { id: string; jobId: string | null }) => {
      const { error } = await supabase.from('invoices').delete().eq('id', id).eq('status', 'draft')
      if (error) throw error
    },
    onSuccess: (_data, variables) => {
      invalidate(variables.jobId, variables.id)
      toast.success('Draft invoice deleted')
    },
    onError: (error) => {
      toast.error('Could not delete the invoice', { description: describeError(error) })
    },
  })
}

export function useRecordPayment() {
  const invalidate = useInvalidate()

  return useMutation({
    mutationFn: async (input: {
      invoiceId: string
      jobId: string | null
      amount: number
      paidOn: string
      method: Payment['method']
      reference: string | null
    }) => {
      const { error } = await supabase.from('payments').insert({
        invoice_id: input.invoiceId,
        amount: input.amount,
        paid_on: input.paidOn,
        method: input.method,
        reference: input.reference,
      })
      if (error) throw error
    },
    onSuccess: (_data, input) => {
      invalidate(input.jobId, input.invoiceId)
      toast.success('Payment recorded')
    },
    onError: (error) => {
      toast.error('Could not record the payment', { description: describeError(error) })
    },
  })
}

export function useDeletePayment() {
  const invalidate = useInvalidate()

  return useMutation({
    mutationFn: async ({ id }: { id: string; invoiceId: string; jobId: string | null }) => {
      const { error } = await supabase.from('payments').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: (_data, variables) => {
      invalidate(variables.jobId, variables.invoiceId)
      toast.success('Payment removed')
    },
    onError: (error) => {
      toast.error('Could not remove the payment', { description: describeError(error) })
    },
  })
}

/** Builds the invoice PDF server side and returns a short-lived signed link. */
export function useGenerateInvoicePdf() {
  const invalidate = useInvalidate()

  return useMutation({
    mutationFn: async ({ invoiceId }: { invoiceId: string; jobId: string | null }): Promise<string> => {
      const { data, error } = await supabase.functions.invoke<{ signedUrl: string; error?: string }>(
        'generate-invoice-pdf',
        { body: { invoiceId } },
      )
      if (error) {
        const response = (error as { context?: Response }).context
        let detail = error instanceof Error ? error.message : 'The PDF could not be generated.'
        try {
          const body = (await response?.json()) as { error?: string } | undefined
          if (body?.error) detail = body.error
        } catch {
          /* keep the generic message */
        }
        throw new Error(detail)
      }
      if (!data?.signedUrl) throw new Error('The PDF was generated but no link came back.')
      return data.signedUrl
    },
    onSuccess: (_url, variables) => invalidate(variables.jobId, variables.invoiceId),
    onError: (error) => {
      toast.error('Could not build the PDF', { description: describeError(error) })
    },
  })
}

/**
 * Saves a draft invoice typed line by line (29 Oct 2026): its lines, totals,
 * note and due date. If it has a payment plan, each instalment keeps its
 * share of the new total (rescaleInstalments), so the plan always adds up.
 */
export function useSaveInvoiceDraft() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (input: {
      invoiceId: string
      jobId: string | null
      lines: EditableLine[]
      totals: LineTotals
      note: string | null
      dueDate: string
      instalments: Instalment[]
    }) => {
      const { error } = await supabase
        .from('invoices')
        .update({
          subtotal: Math.max(0, input.totals.subtotal),
          vat: Math.max(0, input.totals.vat),
          total: Math.max(0, input.totals.subtotal) + Math.max(0, input.totals.vat),
          note: input.note,
          due_date: input.dueDate,
        })
        .eq('id', input.invoiceId)
        .eq('status', 'draft')
      if (error) throw error
      const { error: clearError } = await supabase.from('invoice_line_items').delete().eq('invoice_id', input.invoiceId)
      if (clearError) throw clearError
      const rows = input.lines.filter(isFilled).map((line, index) => ({
        invoice_id: input.invoiceId,
        description: line.description.trim(),
        detail: line.detail.trim() || null,
        quantity: line.quantity,
        unit_price: line.unitPrice,
        line_total: lineAmount(line.quantity, line.unitPrice),
        sort_order: index,
      }))
      if (rows.length) {
        const { error: insertError } = await supabase.from('invoice_line_items').insert(rows)
        if (insertError) throw insertError
      }
      const current = input.instalments.reduce((sum, item) => sum + Math.round(Number(item.amount) * 100), 0)
      if (input.instalments.length && current !== Math.round(input.totals.total * 100)) {
        const scaled = rescaleInstalments(
          input.instalments.map((item) => ({ label: item.label, amount: Number(item.amount), dueDate: item.due_date })),
          input.totals.total,
        )
        await writeInstalments(input.invoiceId, scaled)
      }
    },
    onSuccess: (_data, input) => invalidate(input.jobId, input.invoiceId),
    onError: (error) => toast.error('Could not save the invoice', { description: describeError(error) }),
  })
}
